import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormGroup, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Damage, DamageType, PlayerCharacter, Stat, Trait } from "../../model/character"
import { useContext, useEffect, useState } from "react";
import StatLookupField from "../../components/StatLookupField";

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { NumberField } from "../../components/formFields/NumberField";
import { ArmorAtZeroStat, ArmorStat, EnduranceCurrentStat, EnduranceMaxStat, Resistance } from "../../model/statsConstant";
import Section from "../../components/Section";
import { Modal, ModalAction } from "../../components/Modal";
import { HexList } from "../../components/HexList";
import FormFieldLabel from "../../components/formFields/FormFieldLabel";
import { CharacterContext } from "../../model/characterContext";

export interface TraitDisplayProps {
    character: PlayerCharacter;
    setCharacter: (character: PlayerCharacter) => void;
}

const EditDamage = (props: {
    damageType: DamageType,
    damage: Damage,
    armour: number;
    armourAtZero: number;
    resistance: number;
    onChange: (damage: Damage) => void
}) => {
    const { armour, armourAtZero, resistance } = props;

    const handleChange = (field: string, value: number) => {
        let newDamage = {
            ...props.damage,
            [field]: value
        }
        props.onChange(newDamage);
    }

    return <Stack spacing={1}>
        <FormFieldLabel label="Damage" justifyContent="end">
            <NumberField step={1} min={0} value={props.damage.adjustment} onChange={(v) => handleChange('adjustment', v)} />
        </FormFieldLabel>
        {props.damageType.armourReduces && <>
            <FormFieldLabel label="Armour" justifyContent="end">{armour}</FormFieldLabel>
            <FormFieldLabel label="Armour at 0 endurance" justifyContent="end">{armourAtZero}</FormFieldLabel>
        </>}
        {props.damageType.resistanceReduces && <>
            <FormFieldLabel label="Resistance" justifyContent="end">{resistance}</FormFieldLabel>
        </>}

    </Stack>
}

const AddModal = (props: {
    show: boolean,
    character: PlayerCharacter,
    onAdd: (value: Damage) => void,
    onClose: () => void,
}) => {
    const characterContext = useContext(CharacterContext)!;
    const [ currentDamageType, setCurrentDamageType ] = useState<DamageType | undefined>(undefined);
    const [ currentDamage, setCurrentDamage ] = useState<Damage>({ adjustment:0, targetId: "" });

    const armour = (characterContext.characterDisplay.outfits.find(outfit => outfit.isWorn)?.armor ?? 0) + characterContext.getAdjustment(ArmorStat.id);
    const armourAtZero = (characterContext.characterDisplay.outfits.find(outfit => outfit.isWorn)?.armorAtZero ?? 0) + characterContext.getAdjustment(ArmorAtZeroStat.id);

    const enduranceDamage = characterContext.character.damages.find(d => d.targetId === "endurance")?.adjustment ?? 0;
    const enduranceMaxDamage = characterContext.character.damages.find(d => d.targetId === "enduranceMax")?.adjustment ?? 0;
    const strengthDamage = characterContext.character.damages.find(d => d.targetId === "str")?.adjustment ?? 0;

    const strength = (characterContext.character.attributes.find(a => a.id == "str")?.alloted ?? 0) + characterContext.getAdjustment("str") - strengthDamage;
    
    const enduranceMax = 10 + (Math.max(strength ?? 0, 0)) * 5 + (characterContext.getAdjustment(EnduranceMaxStat.id) ?? 0) - enduranceMaxDamage;
    const endurance = enduranceMax - enduranceDamage;

    const resistance = characterContext.getAdjustment(Resistance.id);

    useEffect(() => {
        if(props.show) {
            setCurrentDamageType(undefined);
        }
    }, [props.show]);

    const handleSubmit = () => {
        let value = currentDamage.adjustment;
        if(currentDamageType?.armourReduces){
            const useArmour = endurance <= 0 ? armourAtZero : armour;
            value -= useArmour;
        }
        if(currentDamageType?.resistanceReduces){
            value -= resistance;
        }
        props.onAdd({
            ...currentDamage,
            adjustment: value
        });
    }

    const handleSelect = (id: string) => {
        setCurrentDamageType(props.character.damageTypes.find(dt => dt.id === id));
        setCurrentDamage({ adjustment: 0, targetId: id});
    }

    const actions: ModalAction[] = [{ label: "Cancel", onClick: props.onClose, size: currentDamageType === undefined ? 12 : 6 }];
    if(currentDamageType !== undefined) actions.push({ label: "Add", onClick: handleSubmit, primary: true });

    return <Modal title="Add Damage" open={props.show} onClose={props.onClose} actions={actions}>
        {currentDamageType === undefined && <HexList items={
            props.character.damageTypes.map(dt => ({id: dt.id, name: dt.name, onClick: handleSelect }))
        } />}
        {currentDamageType !== undefined && <>
            <EditDamage damage={currentDamage} damageType={currentDamageType} onChange={setCurrentDamage} armour={armour} armourAtZero={armourAtZero} resistance={resistance} />
        </>}
    </Modal>
}

const HealModal = (props: {
    show: boolean,
    damage?: Damage,
    onHeal: (value: number) => void,
    onClose: () => void,
}) => {
    const [ healAmount, setHealAmount ] = useState(0);

    const actions: ModalAction[] = [
        { label: "Cancel", onClick: props.onClose, size: 6 },
        { label: "Add", onClick: () => props.onHeal(healAmount), size: 6, primary: true },
    ];

    if(!props.damage) {return <></>}

    return <Modal title="Heal" open={props.show} onClose={props.onClose} actions={actions}>
        <FormFieldLabel label="Amount" justifyContent="end">
            <NumberField step={1} min={1} max={props.damage.adjustment} value={healAmount} onChange={setHealAmount} />
        </FormFieldLabel>
    </Modal>
}

const DamageDisplay = (props: TraitDisplayProps) => {
    const { character, setCharacter } = props;

    const [ showAddModal, setShowAddModal ] = useState(false);
    const [ healIndex, setHealIndex ] = useState<undefined | number>(undefined);

    const handleAdd = () => {
        setShowAddModal(true);
    }

    const handleAddSubmit = (damage: Damage) => {
        const damages = [ ...character.damages ];

        const existing = damages.findIndex(d => d.targetId == damage.targetId);

        if(existing >= 0) {
            damages[existing].adjustment += damage.adjustment;
        } else {
            damages.push(damage);
        }

        setShowAddModal(false);
        setCharacter({ ...character, damages});
    }

    const handleRemoveTrait = ( index: number) => {
        const damages = [ ...character.damages ];
        damages.splice(index, 1);
        setCharacter({ ...character, damages });
    }

    const handleHeal = (index: number, value: number) => {
        const damages = [...character.damages];
        const damage = damages[index];

        damage.adjustment -= value
        setHealIndex(undefined);
        if(damage.adjustment <= 0) {
            handleRemoveTrait(index);
            return;
        }
        setCharacter({ ...character, damages });
    }

    return <Section title="Damage" actions={[
        { action: handleAdd, label: <AddIcon /> }
    ]}>
        <AddModal show={showAddModal} character={character} onAdd={handleAddSubmit} onClose={() => setShowAddModal(false)} />
        <HealModal show={healIndex !== undefined} damage={healIndex !== undefined ? character.damages[healIndex ?? -1] : undefined} onHeal={(a) => handleHeal(healIndex ?? -1, a)} onClose={() => setHealIndex(undefined)} />
        {character.damages.map((damage, index) => <div key={index}>
            <Stack marginLeft={1} direction={"row"} alignItems={"center"} spacing={1}>
                <div>{character.damageTypes.find(a => a.id === damage.targetId)?.name ?? "No damage target selected."}</div>
                <Box flexGrow={1}>{damage.adjustment}</Box>
                <CloseIcon sx={{verticalAlign: "bottom"}} onClick={() => setHealIndex(index)} />
            </Stack>
            </div>)}
    </Section>
}

export default DamageDisplay;